import {
  addDisposer,
  addMiddleware,
  createActionTrackingMiddleware2,
  getEnv,
  getRoot,
  getType,
  isActionContextThisOrChildOf,
  recordPatches,
  types,
  getParent,
  splitJsonPath,
  getSnapshot
} from 'mobx-state-tree';
import equal from 'fast-deep-equal/react';
import set from 'set-value';
import get from 'get-value';

const getOriginal = (original, attributePath) => {
  const pathWithoutIndex = attributePath.slice(0, attributePath.length - 1).join('.');
  const index = attributePath.slice(attributePath.length - 1);
  let value = get(original, pathWithoutIndex);
  const arr = value.map(x => x);
  arr.splice(parseInt(index, 10), 1);
  value = arr;
  const newValue = set(original, pathWithoutIndex, value);
  return newValue;
};

const resolveTypeAfterRemove = (targetType, contextType, call, patch, item, pathParts, prePatchContext) => {
  return prePatchContext;
};

const areEqual = (original, comparer, modelType) => {
  const originalCopy = JSON.parse(JSON.stringify(original));
  const comparerCopy = JSON.parse(JSON.stringify(comparer));

  return equal(originalCopy, comparerCopy);
};

const walkUntilType = (root, targetType, pathParts) => {
  let element = root;
  let leaf;
  let i = 0;
  while (!leaf) {
    element = element[pathParts[i]];
    let type = getType(element);
    if (type._subType) {
      type = type._subType;
    }
    if (type === targetType) {
      leaf = element;
    } else {
      i++;
    }
  }
  return { item: leaf, index: i };
};

const ItemTrackerModel = types
  .model('ItemTrackerModel', {
    modelType: types.string,
    identifier: types.string,
    original: types.frozen(),
    hasChanges: types.boolean,
    isRemoved: types.boolean,
    isNew: types.boolean
  })
  .actions(self => ({
    updateHasChanges(value) {
      self.hasChanges = value;
    },
    updateIsRemoved(value) {
      self.isRemoved = value;
    },
    updateIsNew(value) {
      self.isNew = value;
    }
  }));

const DeltaTracker = types
  .model('DeltaTracker', {
    tracker: types.array(ItemTrackerModel)
  })
  .views(self => ({
    get hasChanges() {
      const itemsWithChanges = self.tracker.filter(
        t => t.hasChanges === true && t.isNew !== true && t.isRemoved !== true
      );
      const newItems = self.tracker.filter(t => t.isNew === true && t.isRemoved !== true);
      const removedItems = self.tracker.filter(t => t.isRemoved === true && t.isNew !== true);

      return itemsWithChanges.length > 0 || newItems.length > 0 || removedItems.length > 0;
    }
  }))
  .actions(self => {
    const isChildOfInitiateStore = call => {
      if (call.parentCall) {
        return isChildOfInitiateStore(call.parentCall);
      }
      if (call.name === 'initiateStore') {
        return true;
      }
      return false;
    };

    const deltaRecorderMiddleware = createActionTrackingMiddleware2({
      filter(call) {
        if (call.env) {
          // already recording
          return false;
        }
        if (call.context === self) {
          // skip actions on self
          return false;
        }
        if (call.name === 'initiateStore') {
          // skip if we are initializing the store
          return false;
        }
        if (call.name === 'updateVersion') {
          // skip if we are  just updating the entity version
          return false;
        }
        if (isChildOfInitiateStore(call)) {
          // skip if action is child action from initializeStore
          return false;
        }
        if (getType(call.context).name === 'ItemTrackerModel' || getType(call.context).name === 'ItemTrackerModel[]') {
          // don't record the tracker items
          return false;
        }

        return true;
      },
      onStart(call) {
        const recorder = recordPatches(call.tree, (_patch, _inversePatch, actionContext) => {
          return !!actionContext && isActionContextThisOrChildOf(actionContext, call.id);
        });
        recorder.resume();
        call.env = {
          recorder,
          prePatchContext: call.context.toJSON()
        };
      },
      onFinish(call, error) {
        const recorder = call.env.recorder;
        const prePatchContext = call.env.prePatchContext;
        call.env = undefined;
        recorder.stop();
        if (error === undefined) {
          self.trackState2(recorder, call, prePatchContext);
        } else {
          recorder.undo();
        }
      }
    });
    return {
      clearTrackedState() {
        self.tracker = [];
      },
      updateTrackedStateForTests(listOfChanges) {
        self.tracker = listOfChanges;
      },
      trackState2(recorder, call, prePatchContext) {
        if (recorder.patches.length === 0) {
          return;
        }

        recorder.patches.forEach(patch => {
          if (!patch.path.startsWith('/tracker')) {
            // eslint-disable-next-line default-case
            switch (patch.op) {
              case 'add':
                self.ItemAdded(patch, call, prePatchContext);
                break;
              case 'remove':
                self.ItemRemoved(patch, call, prePatchContext);
                break;
              case 'replace':
                self.ItemModified(call, prePatchContext);
                break;
            }
          }
        });
      },
      ItemAdded(patch) {
        const pathParts = splitJsonPath(patch.path);

        let modelType = getType(getRoot(self)[pathParts[0]]);
        if (modelType._subType) {
          modelType = modelType._subType;
        }

        if (pathParts.length > 2) {
          // it's a modification of a property on a model
          const { item, index } = walkUntilType(getRoot(self), modelType, pathParts);
          const identifier = item[modelType.identifierAttribute];
          let found;
          if (identifier) {
            found = self.tracker.find(t => t.identifier === identifier && t.modelType === modelType.name);
          } else {
            found = self.tracker.find(t => t.modelType === modelType.name);
          }

          if (found) {
            found.updateHasChanges(!areEqual(found.original, item.toJSON(), modelType.name));
          } else {
            const original = getOriginal(JSON.parse(JSON.stringify(getSnapshot(item))), pathParts.slice(index + 1));
            self.tracker.push({
              modelType: modelType.name,
              identifier: identifier || 'n/a',
              original: original,
              hasChanges: true,
              isRemoved: false,
              isNew: false
            });
          }
        } else {
          // adding a new model
          self.tracker.push({
            modelType: modelType.name,
            identifier: patch.value[modelType.identifierAttribute] || 'n/a',
            original: patch.value,
            hasChanges: false,
            isRemoved: false,
            isNew: true
          });
        }
      },
      ItemRemoved(patch, call, prePatchContext) {
        const pathParts = splitJsonPath(patch.path);

        let modelType = getType(getRoot(self)[pathParts[0]]);
        if (modelType._subType) {
          modelType = modelType._subType;
        }

        if (pathParts.length > 2) {
          // it's a modification of a property on a model
          const { item, index } = walkUntilType(getRoot(self), modelType, pathParts);
          const identifier = item[modelType.identifierAttribute];
          let foundByIdentifier;
          if (identifier) {
            foundByIdentifier = self.tracker.find(t => t.identifier === identifier && t.modelType === modelType.name);
          } else {
            foundByIdentifier = self.tracker.find(t => t.modelType === modelType.name);
          }
          if (foundByIdentifier) {
            foundByIdentifier.updateHasChanges(!areEqual(foundByIdentifier.original, item.toJSON(), modelType.name));
          } else {
            //const original = getOriginal(Object.assign({}, item.toJSON()), pathParts.slice(index + 1));
            const contextType = getType(call.context);
            const original =
              contextType.name === modelType.name
                ? prePatchContext
                : resolveTypeAfterRemove(
                    modelType,
                    contextType,
                    call,
                    patch,
                    Object.assign({}, item.toJSON()),
                    pathParts.slice(index + 1),
                    prePatchContext
                  );
            self.tracker.push({
              modelType: modelType.name,
              identifier: identifier || 'n/a',
              original: original,
              hasChanges: true,
              isRemoved: false,
              isNew: false
            });
          }
        } else {
          // removal of model
          const modelType = getType(call.context);
          const context = call.context.toJSON();
          const contextIdentifier = context[modelType.identifierAttribute];
          let foundByContextIdentifier;
          if (contextIdentifier) {
            foundByContextIdentifier = self.tracker.find(
              t => t.identifier === contextIdentifier && t.modelType === modelType.name
            );
          } else {
            foundByContextIdentifier = self.tracker.find(t => t.modelType === modelType.name);
          }
          if (foundByContextIdentifier) {
            foundByContextIdentifier.updateIsRemoved(true);
          } else {
            self.tracker.push({
              modelType: modelType.name,
              identifier: context[modelType.identifierAttribute] || 'n/a',
              original: prePatchContext,
              hasChanges: false,
              isRemoved: true,
              isNew: false
            });
          }
        }
      },
      ItemModified(call, prePatchContext) {
        const modelType = getType(call.context);
        const identifier = call.context[modelType.identifierAttribute];
        let found;
        if (identifier) {
          found = self.tracker.find(t => t.identifier === identifier && t.modelType === modelType.name);
        } else {
          found = self.tracker.find(t => t.modelType === modelType.name);
        }
        if (found) {
          found.updateHasChanges(!areEqual(found.original, call.context.toJSON(), modelType.name));
        } else {
          self.tracker.push({
            modelType: modelType.name,
            identifier: identifier || 'n/a',
            original: prePatchContext,
            hasChanges: true,
            isRemoved: false,
            isNew: false
          });
        }
      },
      afterCreate() {
        const parent = getParent(self);
        const selfRoot = getRoot(self);
        const targetStore = parent || getEnv(self).targetStore || selfRoot;
        if (targetStore === self) {
          throw new Error(
            "DeltaTracker should be created as part of a tree, or with `targetStore` in it's environment"
          );
        }
        addDisposer(self, addMiddleware(targetStore, deltaRecorderMiddleware, false));
      }
    };
  });

export default DeltaTracker;
